import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { firstValueFrom } from 'rxjs'

import { MatButtonModule } from '@angular/material/button'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatSelectModule } from '@angular/material/select'

import { ApiError } from '../../../../types/apiError'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { BulkProductShareCsvTemplate } from '../../../../types/productSharingCsvTemplates'
import { ProductSharingCsvTemplateService } from '../../../../services/productSharingCsvTemplate.service'
import { ReviewSharedProductsComponent } from './review-shared-products/review-shared-products.component'
import { RouteService } from '../../../../services/route.service'
import { ROUTES } from '../../../../constants/routes'
import { ProductService } from '../../../../services/product.service'
import {
  BulkProductShareCsvValidateResponse,
  BulkCreateProductSharesRequest,
} from '../../../../types/products'

@Component({
  selector: 'app-bulk-share',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    ReviewSharedProductsComponent,
  ],
  templateUrl: './bulk-share.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bulk-share.component.scss',
})
export class BulkShareComponent implements OnInit {
  private fb = inject(FormBuilder)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  translationService = inject(TranslationService)
  private templateService = inject(ProductSharingCsvTemplateService)
  private routeService = inject(RouteService)
  private productService = inject(ProductService)

  @ViewChild('shareCsvFile') shareCsvFile?: ElementRef<HTMLInputElement>

  templates: Array<BulkProductShareCsvTemplate> = []
  hasSelectedFile = false

  bulkShareForm = this.fb.group({
    template: ['', Validators.required],
  })

  reviewResponse: BulkProductShareCsvValidateResponse = {
    totalRows: 0,
    validRows: 0,
    rows: [],
  }

  constructor() {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)
  }

  async ngOnInit(): Promise<void> {
    await this.loadTemplates()
  }

  /**
   * Fetches CSV templates for selection.
   * @returns {Promise<void>}
   */
  async loadTemplates(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      this.templates = await firstValueFrom(this.templateService.getAll())
      this.spinnerService.stopSpinner()
    } catch {
      this.spinnerService.stopSpinner()
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getSomethingWentWrongText(),
        this.translationService.getErrorOccurredText(),
      )
    }
  }

  /**
   * Tracks whether a file has been selected.
   * @param {Event} event File change event.
   * @returns {void}
   */
  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement
    this.hasSelectedFile = !!input.files?.[0]
  }

  /**
   * Validates the form and submits the CSV for server-side validation, returning rows for review.
   * @returns {Promise<void>}
   */
  async onSubmit(): Promise<void> {
    if (this.bulkShareForm.invalid) {
      return
    }

    const templateId = this.bulkShareForm.value.template
    const file = this.shareCsvFile?.nativeElement.files?.[0]

    if (!templateId || !file) {
      return
    }

    try {
      this.spinnerService.startSpinner()

      const formData = new FormData()
      formData.append('template', templateId)
      formData.append('shareCsvFile', file)

      const res = await firstValueFrom(
        this.productService.previewBulkProductShares(formData),
      )

      this.reviewResponse = {
        totalRows: res.totalRows ?? 0,
        validRows: res.validRows ?? 0,
        rows: res.rows ?? [],
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets the form, selected file, and review results.
   * @returns {void}
   */
  reset(): void {
    this.bulkShareForm.reset()

    if (this.shareCsvFile) {
      this.shareCsvFile.nativeElement.value = ''
    }

    this.hasSelectedFile = false

    this.reviewResponse = {
      totalRows: 0,
      validRows: 0,
      rows: [],
    }
  }

  /**
   * Creates share records for all valid rows after user confirmation.
   * @returns {Promise<void>}
   */
  async createShares(): Promise<void> {
    try {
      this.spinnerService.startSpinner()

      const request: BulkCreateProductSharesRequest = {
        shares: this.reviewResponse.rows
          .filter(
            (row) =>
              row.readyToCreate &&
              !!row.resolvedProductId &&
              !!row.resolvedToCompanyId,
          )
          .map((row) => ({
            productId: row.resolvedProductId!,
            toCompanyId: row.resolvedToCompanyId!,
            overrideProductSupplier: row.overrideProductSupplier,
            overrideComponentSuppliers: row.overrideComponentSuppliers,
          })),
      }

      if (request.shares.length === 0) {
        this.spinnerService.stopSpinner()
        return
      }

      await firstValueFrom(this.productService.createBulkProductShares(request))

      this.spinnerService.stopSpinner()

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getShareProductsText(),
        this.translationService.getBulkShareSuccessText(),
      )

      this.reset()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
